import {AyTracking} from './ay-tracking';
import {TrackingClientStateHolder} from './providers/gtm/helpers/clientState';
import {shouldPushToDataLayerObject} from './providers/gtm/helpers/datalayer';
import {LiveShoppingTrackParams} from './providers/gtm/trackingHandlers/trackLiveShoppingPlayer';
import {trackSearchParams} from './providers/gtm/trackingHandlers/trackSearch';
import {
  ArticleDetailPageContext,
  CategoryDetailPageContext,
  MainFilterSelectionEvent,
  OutfitDetailPageContext,
  QuickFilterSelectionEvent,
  RESET_FILTER_SELECTION,
  StoryDetailPageContext,
  TrackingBasicElementContext,
  TrackingBasketItem,
  TrackingCategoryPageDetails,
  TrackingContentInteraction,
  TrackingCustomerData,
  TrackingElementContext,
  TrackingFeatureTypes,
  TrackingLocaleData,
  TrackingPageContext,
  TrackingPageTypeWithId,
  TrackingPageTypeWithoutId,
  TrackingProduct,
  TrackingProductCategory,
  TrackingProductStreamElementContext,
  TrackingProductStreamSortType,
  TrackingProductStreamTileViewType,
  TrackingPromotion,
  TrackingSectionContext,
  TrackingTransaction,
  TrackingVariant,
  TrackingWishlistItem,
  membershipCard,
  originClick,
} from './types';
import {FilterActions, FilterLabels} from './types/tracking-filter-selection';
import {TrackingRecommendedSizes} from './types/tracking-recommended-sizes';
import {TrackingVoucherData} from './types/tracking-voucher-data';

/**
 * @deprecated
 * this is what we recognize as legacy tracking client, for current tracking client please check ay-tracking-client.ts
 */
export class AYTrackingClient {
  private readonly providers: Array<Partial<AyTracking>>;
  private state: TrackingClientStateHolder;

  constructor(
    providers: Array<Partial<AyTracking>>,
    state: TrackingClientStateHolder
  ) {
    this.providers = providers;
    this.state = state;
  }

  private callForAllProvider = (f: (provider: Partial<AyTracking>) => void) => {
    for (const provider of this.providers) {
      try {
        f(provider);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    }
  };

  /**  @param page TrackingInitialPageContext  */
  trackContentView = ({
    page,
    customer,
  }: {
    page: TrackingPageContext;
    breadcrumbsPath?: string | null;
    customer?: TrackingCustomerData;
  }): void => {
    this.callForAllProvider(
      provider =>
        provider.trackContentView &&
        shouldPushToDataLayerObject(
          provider.trackContentView({
            page,
            customer,
          })(this.state),
          this.state
        )
    );
  };

  /**
   * @param page TrackingInitialPageContext
   * @param  categoryDetail TrackingCategoryPageDetails
   * */
  trackCategoryView = ({
    page,
    categoryDetail,
  }: {
    page: CategoryDetailPageContext;
    categoryDetail: TrackingCategoryPageDetails;
  }): void => {
    this.callForAllProvider(
      provider =>
        provider.trackCategoryView &&
        shouldPushToDataLayerObject(
          provider.trackCategoryView({page, categoryDetail})(this.state),
          this.state
        )
    );
  };

  /**
   *
   * @param page ArticleDetailPageContext
   * @param product TrackingProduct
   * @param productCategories TrackingProductCategory[]
   */
  trackAdpView = ({
    page,
    product,
    productCategories,
  }: {
    page: ArticleDetailPageContext;
    product: TrackingProduct;
    productCategories: TrackingProductCategory[];
  }): void => {
    this.callForAllProvider(
      provider =>
        provider.trackAdpView &&
        shouldPushToDataLayerObject(
          provider.trackAdpView({page, product, productCategories})(this.state),
          this.state
        )
    );
  };

  /**
   *
   * @param product TrackingProduct
   * @param variant TrackingVariant
   * @param productCategories TrackingProductCategory[]
   */
  trackAdpSizeChange = ({
    product,
    variant,
    productCategories,
  }: {
    product: TrackingProduct;
    variant: TrackingVariant;
    productCategories: TrackingProductCategory[];
  }): void => {
    this.callForAllProvider(
      provider =>
        provider.trackAdpSizeChange &&
        shouldPushToDataLayerObject(
          provider.trackAdpSizeChange({
            product,
            variant,
            productCategories,
          })(this.state),
          this.state
        )
    );
  };

  trackStoryView = ({
    page,
    customer,
  }: {
    page: StoryDetailPageContext;
    customer?: TrackingCustomerData;
  }): void => {
    this.callForAllProvider(
      provider =>
        provider.trackStoryView &&
        shouldPushToDataLayerObject(
          provider.trackStoryView({page, customer})(this.state),
          this.state
        )
    );
  };

  /**  @param page OutfitDetailPageContext  */
  trackOutfitView = ({
    page,
    customer,
  }: {
    page: OutfitDetailPageContext;
    customer?: TrackingCustomerData;
  }): void => {
    this.callForAllProvider(
      provider =>
        provider.trackOutfitView &&
        shouldPushToDataLayerObject(
          provider.trackOutfitView({page, customer})(this.state),
          this.state
        )
    );
  };

  /**
   *
   * @param customer TrackingCustomerData
   * @param basketCount number
   * @param wishlistCount number
   * @param basketItems {id:number, quantity: number}[]
   */
  trackCustomerData = ({
    customer,
    basketCount: _basketCount,
    wishlistCount: _wishlistCount,
    basketItems: _basketItems,
  }: {
    customer: TrackingCustomerData;
    basketCount: number;
    wishlistCount: number;
    basketItems: {id: number; quantity: number}[];
  }): void => {
    this.callForAllProvider(
      provider =>
        provider.trackCustomerData &&
        shouldPushToDataLayerObject(
          provider.trackCustomerData({
            customer,
          })(this.state),
          this.state
        )
    );
  };

  /**
   *
   * @param type 'impression' | 'click'
   * @param section TrackingSectionContext
   * @param element TrackingElementContext | TrackingProductStreamElementContext
   * @param product TrackingProduct
   */
  trackProductInteraction = ({
    type,
    section,
    element,
    product,
  }: {
    type: 'impression' | 'click';
    section: TrackingSectionContext;
    element: TrackingElementContext | TrackingProductStreamElementContext;
    product: TrackingProduct;
  }): void => {
    this.callForAllProvider(
      provider =>
        provider.trackProductInteraction &&
        shouldPushToDataLayerObject(
          provider.trackProductInteraction({type, section, element, product})(
            this.state
          ),
          this.state
        )
    );
  };

  /**
   *
   * @param type 'add-to-basket' | 'quantity-update' | 'removed-from-basket'
   * @param section TrackingSectionContext
   * @param element TrackingBasicElementContext
   * @param basketItem TrackingBasketItem
   */
  trackBasketAction = ({
    type,
    section,
    element,
    basketItem,
    badges,
  }: {
    type: 'add-to-basket' | 'quantity-update' | 'removed-from-basket';
    section: TrackingSectionContext;
    element: TrackingBasicElementContext;
    basketItem: TrackingBasketItem;
    badges: string[];
  }): void => {
    this.callForAllProvider(
      provider =>
        provider.trackBasketAction &&
        provider.trackBasketAction({
          type,
          section,
          element,
          basketItem,
          badges,
        })(this.state)
    );
  };

  /**
   *
   * @param type 'add-to-wishlist' | 'removed-from-wishlist'
   * @param section TrackingSectionContext
   * @param element TrackingElementContext | TrackingProductStreamElementContext
   * @param wishlistItem TrackingWishlistItem
   */
  trackWishlistAction = ({
    type,
    section,
    element,
    wishlistItem,
  }: {
    type: 'add-to-wishlist' | 'removed-from-wishlist';
    section: TrackingSectionContext;
    element: TrackingElementContext | TrackingProductStreamElementContext;
    wishlistItem: TrackingWishlistItem;
  }): void => {
    this.callForAllProvider(
      provider =>
        provider.trackWishlistAction &&
        shouldPushToDataLayerObject(
          provider.trackWishlistAction({
            type,
            section,
            element,
            wishlistItem,
          })(this.state),
          this.state
        )
    );
  };

  /**
   *
   * @param type 'impression' | 'click'
   * @param section TrackingSectionContext
   * @param element TrackingElementContext
   * @param promotion TrackingPromotion
   */
  trackPromotionInteraction = ({
    type,
    section,
    element,
    promotion,
  }: {
    type: 'impression' | 'click';
    section: TrackingSectionContext;
    element: TrackingElementContext;
    promotion: TrackingPromotion;
  }): void => {
    if (element.type === 'product-list-element-content') {
      // eslint-disable-next-line no-console
      console.error(
        'Wrongly using "product-list-element-content" element context for a non-product reco'
      );
      return;
    }

    this.callForAllProvider(
      provider =>
        provider.trackPromotionInteraction &&
        shouldPushToDataLayerObject(
          provider.trackPromotionInteraction({
            type,
            section,
            element,
            promotion,
          })(this.state),
          this.state
        )
    );
  };

  /**
   *
   * @param customer TrackingCustomerData
   * @param transactions TrackingTransaction
   * @param recommendedSizes TrackingRecommendedSizes
   * @param localeData TrackingLocaleData
   */
  trackOrderSuccessPage = ({
    customer,
    transactions,
    recommendedSizes,
    localeData,
    voucher,
  }: {
    customer: TrackingCustomerData;
    transactions: TrackingTransaction;
    recommendedSizes: TrackingRecommendedSizes;
    localeData: TrackingLocaleData;
    voucher?: TrackingVoucherData;
  }): void => {
    this.callForAllProvider(provider => {
      if (provider.trackOrderSuccessPage) {
        const data = provider.trackOrderSuccessPage({
          customer,
          transactions,
          recommendedSizes,
          localeData,
          voucher,
        })(this.state);
        if (Array.isArray(data)) {
          data.forEach(item => shouldPushToDataLayerObject(item, this.state));
        } else {
          shouldPushToDataLayerObject(data, this.state);
        }
      }
    });
  };

  trackOspError = ({message}: {message: string}) => {
    this.callForAllProvider(
      provider =>
        provider.trackOspError &&
        shouldPushToDataLayerObject(
          provider.trackOspError({message}),
          this.state
        )
    );
  };

  trackHouston = ({message}: {message: string}) => {
    this.callForAllProvider(
      provider =>
        provider.trackHouston &&
        shouldPushToDataLayerObject(
          provider.trackHouston({message}),
          this.state
        )
    );
  };

  /** @param selection MainFilterSelectionEvent */
  trackMainFilterSelection = ({
    selection,
    action,
    label,
  }: {
    selection: MainFilterSelectionEvent | typeof RESET_FILTER_SELECTION;
    action?: FilterActions;
    label?: FilterLabels;
  }): void => {
    const actionToPush = action ?? 'click';
    const filterLabel = label ?? 'filter_modal';
    this.callForAllProvider(provider =>
      shouldPushToDataLayerObject(
        provider.trackMainFilterSelection?.({
          selection,
          action: actionToPush,
          label: filterLabel,
        })(this.state),
        this.state
      )
    );
  };

  /** @param selection QuickFilterSelectionEvent */
  trackQuickFilterSelection = ({
    selection,
    action,
    label,
  }: {
    selection: QuickFilterSelectionEvent;
    action?: FilterActions;
    label: FilterLabels;
  }): void => {
    const actionToPush = action ?? 'click';
    const filterLabel = label ?? 'filter_modal';

    this.callForAllProvider(provider =>
      shouldPushToDataLayerObject(
        provider.trackQuickFilterSelection?.({
          selection,
          action: actionToPush,
          label: filterLabel,
        })(this.state),
        this.state
      )
    );
  };

  /** @param sort TrackingProductStreamSortType */
  trackProductListSort = ({
    sort,
  }: {
    sort: TrackingProductStreamSortType;
  }): void => {
    this.callForAllProvider(
      provider =>
        provider.trackProductListSort &&
        shouldPushToDataLayerObject(
          provider.trackProductListSort({sort})(this.state),
          this.state
        )
    );
  };

  /** @param sort TrackingProductStreamTileViewType */
  trackProductListTileView = ({
    tileView,
  }: {
    tileView: TrackingProductStreamTileViewType;
  }): void => {
    this.callForAllProvider(
      provider =>
        provider.trackProductListTileView &&
        shouldPushToDataLayerObject(
          provider.trackProductListTileView({tileView})(this.state),
          this.state
        )
    );
  };

  /** @param interaction TrackingContentInteraction */
  trackContentInteraction = ({
    interaction,
  }: {
    interaction: TrackingContentInteraction;
  }): void => {
    this.callForAllProvider(
      provider =>
        provider.trackContentInteraction &&
        shouldPushToDataLayerObject(
          provider.trackContentInteraction({interaction})(),
          this.state
        )
    );
  };

  /**
   *  @param product TrackingProduct
   *  @param action 'start' | 'complete' | 'close'
   * */
  trackFitAnalytics = ({
    product,
    action,
  }: {
    product: TrackingProduct;
    action: 'start' | 'complete' | 'close';
  }): void => {
    this.callForAllProvider(
      provider =>
        provider.trackFitAnalytics &&
        shouldPushToDataLayerObject(
          provider.trackFitAnalytics({product, action})(this.state),
          this.state
        )
    );
  };

  /** @param action 'close' | 'accept' | 'link' */
  trackCookieBanner = ({
    action,
  }: {
    action: 'close' | 'accept' | 'link';
  }): void => {
    this.callForAllProvider(
      provider =>
        provider.trackCookieBanner &&
        shouldPushToDataLayerObject(
          provider.trackCookieBanner({action}),
          this.state
        )
    );
  };

  /** @param action 'subscribed' | 'unsubscribed' */
  trackNewsletter = ({
    action,
    email,
  }: {
    action: 'subscribed' | 'unsubscribed';
    email: string;
  }): void => {
    this.callForAllProvider(
      provider =>
        provider.trackNewsletter &&
        shouldPushToDataLayerObject(
          provider.trackNewsletter({action, email})(this.state),
          this.state
        )
    );
  };

  /** @param feature TrackingFeatureTypes */
  trackShopFeature = ({feature}: {feature: TrackingFeatureTypes}): void => {
    this.callForAllProvider(
      provider =>
        provider.trackShopFeature &&
        shouldPushToDataLayerObject(
          provider.trackShopFeature({feature})(),
          this.state
        )
    );
  };

  /**
   * @param label search term
   * @param searchTermCompleted search term by the shop
   * @param action historyTerm | suggestedProduct | suggestedBrand | suggestedCategory | searchButton | searchHotkey
   * @param searchDestination URL the user is sent to after submitting the search form
   */
  trackSearch = ({
    name,
    label,
    searchTermCompleted,
    action,
    searchDestination,
    searchType,
  }: trackSearchParams): void => {
    this.callForAllProvider(
      provider =>
        provider.trackSearch &&
        shouldPushToDataLayerObject(
          provider.trackSearch({
            name,
            label,
            searchTermCompleted,
            action,
            searchDestination,
            searchType,
          }),
          this.state
        )
    );
  };

  /**
   * @param action 'close'
   * @param headline string
   */
  trackNotification = ({
    action,
    headline,
  }: {
    action: 'close';
    headline: string;
  }): void => {
    this.callForAllProvider(
      provider =>
        provider.trackNotification &&
        shouldPushToDataLayerObject(
          provider.trackNotification({action, headline}),
          this.state
        )
    );
  };

  /**
   * @param testName string
   * @param testVariation string
   */
  trackABTestGroupAssignment = ({
    testName,
    testVariation,
  }: {
    testName: string;
    testVariation: string;
  }): void => {
    this.callForAllProvider(
      provider =>
        provider.trackABTestGroupAssignment &&
        shouldPushToDataLayerObject(
          provider.trackABTestGroupAssignment({
            testName,
            testVariation,
          })(this.state),
          this.state
        )
    );
  };

  /**
   * @param testName string
   * @param testVariation string
   */
  trackExperimentActivation = ({
    testName,
    testVariation,
  }: {
    testName: string;
    testVariation: string;
  }): void => {
    this.callForAllProvider(
      provider =>
        provider.trackExperimentActivation &&
        shouldPushToDataLayerObject(
          provider.trackExperimentActivation({
            testName,
            testVariation,
          })(),
          this.state
        )
    );
  };

  /**
   * @param action 'show' | 'impression' | 'submit' | 'fail' | 'close'
   * @param paymentMethod 'paypal_instant' | 'ratepay_invoice'
   */
  trackExpressCheckout = ({
    action,
    paymentMethod,
  }: {
    action: 'show' | 'impression' | 'submit' | 'fail' | 'close';
    paymentMethod: 'paypal_instant' | 'ratepay_invoice';
  }): void => {
    this.callForAllProvider(
      provider =>
        provider.trackExpressCheckout &&
        shouldPushToDataLayerObject(
          provider.trackExpressCheckout({action, paymentMethod}),
          this.state
        )
    );
  };

  trackSearchSuggest = ({searchTerm}: {searchTerm: string}): void => {
    this.callForAllProvider(
      provider =>
        provider.trackSearchSuggest &&
        shouldPushToDataLayerObject(
          provider.trackSearchSuggest({
            searchTerm,
          }),
          this.state
        )
    );
  };

  /**
   * @param channels string[]
   * @param secondaryChannels string[]
   * @param otherText string
   */
  trackMediaPollResult = ({
    channels,
    secondaryChannels,
    otherText,
  }: {
    channels: string[];
    secondaryChannels: string[];
    otherText?: string;
  }) => {
    this.callForAllProvider(
      provider =>
        provider.trackMediaPollResult &&
        shouldPushToDataLayerObject(
          provider.trackMediaPollResult({
            channels,
            secondaryChannels,
            otherText,
          }),
          this.state
        )
    );
  };

  trackMembership = ({
    action,
    card,
  }: {
    action: 'add' | 'remove';
    card: membershipCard;
  }) => {
    this.callForAllProvider(
      provider =>
        provider.trackMembership &&
        shouldPushToDataLayerObject(
          provider.trackMembership({
            action,
            card,
          }),
          this.state
        )
    );
  };

  trackAccountRegistration = ({
    action,
  }: {
    action: 'registration_submitted_successfully';
  }) => {
    this.callForAllProvider(
      provider =>
        provider.trackAccountRegistration &&
        shouldPushToDataLayerObject(
          provider.trackAccountRegistration({
            action,
          }),
          this.state
        )
    );
  };

  trackHotDrops = ({
    label,
  }: {
    label: 'all' | 'upcoming' | 'active' | 'past' | 'last';
  }) => {
    this.callForAllProvider(
      provider =>
        provider.trackHotDrops &&
        shouldPushToDataLayerObject(
          provider.trackHotDrops({
            label,
          }),
          this.state
        )
    );
  };

  trackProductReview = ({
    action,
    productId,
  }: {
    action: 'impression' | 'click' | 'submit';
    productId: string;
  }) => {
    this.callForAllProvider(
      provider =>
        provider.trackProductReview &&
        shouldPushToDataLayerObject(
          provider.trackProductReview({
            action,
            productId,
          }),
          this.state
        )
    );
  };

  trackOriginClick = ({origin}: {origin: originClick}) => {
    this.callForAllProvider(
      provider =>
        provider.trackOriginClick &&
        provider.trackOriginClick({
          origin,
        })(this.state)
    );
  };

  trackLiveShoppingPlayer = ({
    showId,
    interactionType,
    id,
    broadcastLength,
    showLength,
  }: LiveShoppingTrackParams) => {
    this.callForAllProvider(
      provider =>
        provider.trackLiveShoppingPlayer &&
        shouldPushToDataLayerObject(
          provider.trackLiveShoppingPlayer({
            showId,
            interactionType,
            id,
            broadcastLength,
            showLength,
          }),
          this.state
        )
    );
  };

  replayLastContentViewBefore = (
    pageType: TrackingPageTypeWithId | TrackingPageTypeWithoutId
  ): void => {
    this.callForAllProvider(
      provider =>
        provider.replayLastContentViewBefore &&
        shouldPushToDataLayerObject(
          provider.replayLastContentViewBefore(pageType)(this.state),
          this.state
        )
    );
  };
}
